/**
 * The `/team` tab vocabulary — the shape `engagement-tabs.ts` established and
 * `limits-tabs.ts` repeated.
 *
 * Pure and React-free on purpose: the page, the settings-search registry and the
 * tests all have to agree on these strings, and a literal repeated in three
 * places is a literal that eventually disagrees with itself.
 *
 * **Why this exists at all.** `/team`'s tabs were an uncontrolled
 * `<Tabs defaultValue="members">` that read nothing from the URL, so no tab but
 * the first could be linked — not from an email, not from a support reply, and
 * not from the settings search, which is what surfaced it. "Where do I change
 * somebody's role" and "where do I cancel an invite" are different questions
 * with the same answer under that model: the members tab, and hunt.
 */

export const TEAM_PATH = '/team';

/**
 * Ordered left to right: who is here, what each role may do, and who has been
 * asked. The order is the page's own argument and predates this module.
 */
export const TEAM_TABS = ['members', 'roles', 'invitations'] as const;
export type TeamTab = (typeof TEAM_TABS)[number];

/** The landing tab — the roster, which every workspace has. */
export const DEFAULT_TEAM_TAB: TeamTab = 'members';

/** The query param carrying the tab, matching every other hub's `?tab=`. */
export const TEAM_TAB_PARAM = 'tab';

export function isTeamTab(value: string): value is TeamTab {
  return (TEAM_TABS as readonly string[]).includes(value);
}

/**
 * The tab a URL asks for. An unknown or absent value falls back to the landing
 * tab rather than rendering a blank surface — a stale bookmark from a rename
 * should still show the page.
 */
export function readTeamTab(search: URLSearchParams): TeamTab {
  const raw = search.get(TEAM_TAB_PARAM) ?? '';
  return isTeamTab(raw) ? raw : DEFAULT_TEAM_TAB;
}
